import React, { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { ChevronDown, Star, CreditCard, Link2, Code2, Bell, TrendingUp, Shield, Smartphone, BarChart2, Globe, Megaphone, Share2, FileText, ArrowRight, CheckCircle2, Zap, Users, Building2, Award, Layers, HelpCircle } from 'lucide-react'
import Navbar from '../components/Common/Navbar'
import Footer from '../components/Common/Footer'

const B = 'https://pixner.net/bankio/main/assets/images'

/* ── Fade up with stagger support ── */
const FadeUp = ({ children, delay = 0, className = '', once = true }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once, margin: '-50px' })
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  )
}

/* ── Fade in (for horizontal slides) ── */
const FadeIn = ({ children, delay = 0, x = 0, className = '' }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-50px' })
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, x }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  )
}

/* ── Tilt card wrapper ── */
const TiltCard = ({ children, className = '' }) => {
  const cardRef = useRef(null)
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const rotateX = useSpring(useTransform(y, [-60, 60], [8, -8]), { stiffness: 300, damping: 30 })
  const rotateY = useSpring(useTransform(x, [-60, 60], [-8, 8]), { stiffness: 300, damping: 30 })

  const handleMouseMove = (e) => {
    const rect = cardRef.current.getBoundingClientRect()
    x.set(e.clientX - rect.left - rect.width / 2)
    y.set(e.clientY - rect.top - rect.height / 2)
  }
  const handleMouseLeave = () => { x.set(0); y.set(0) }

  return (
    <motion.div ref={cardRef} className={className}
      style={{ rotateX, rotateY, transformStyle: 'preserve-3d', perspective: 800 }}
      onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
      {children}
    </motion.div>
  )
}

/* ── Count-up hook ── */
function useCountUp(target, duration = 1800, start = false) {
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (!start) return
    let startTime = null
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp
      const progress = Math.min((timestamp - startTime) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setCount(Math.floor(eased * target))
      if (progress < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }, [start, target, duration])
  return count
}

/* ── Stats strip ── */
const stats = [
  { icon: <Users size={22} />, num: 200, suffix: '+', label: 'Businesses Served' },
  { icon: <Zap size={22} />, num: 99, suffix: '.9%', label: 'Uptime SLA' },
  { icon: <Building2 size={22} />, num: 50, suffix: '+', label: 'Banks Supported' },
  { icon: <Award size={22} />, num: 24, suffix: 'h', label: 'Account Activation' },
]

function StatCard({ icon, num, suffix, label }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true })
  const count = useCountUp(num, 1600, inView)
  return (
    <motion.div ref={ref} className="lp-stat-card"
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -4, boxShadow: '0 16px 40px rgba(26,77,190,0.18)' }}>
      <div className="lp-stat-icon">{icon}</div>
      <div className="lp-stat-num">{count}{suffix}</div>
      <div className="lp-stat-label">{label}</div>
    </motion.div>
  )
}

/* ── FAQ ── */
const faqs = [
  { q: 'How do I create an A1XPAY account?', a: 'Click "Open Account", fill in your details, and complete KYC. Most accounts are activated within 24 hours.' },
  { q: 'What payment methods does A1XPAY support?', a: 'UPI, debit/credit cards (Visa, Mastercard, RuPay), net banking (50+ banks), and all major wallets.' },
  { q: 'What are the transaction fees?', a: 'UPI is 0% for transactions above ₹2,000. Cards start at 0.4%. See our Pricing page for the full MDR table.' },
  { q: 'Do you provide an API for integration?', a: 'Yes — REST API with SDKs for PHP, Node.js, Python, and React Native. Sandbox environment included.' },
  { q: 'Is A1XPAY secure?', a: 'Yes. All card data is encrypted and your servers never touch raw card numbers. We follow industry-standard security practices.' },
  { q: 'How do I reach support?', a: 'Call +91 99103 73510 (Mon–Sat, 10 AM–7 PM IST) or email support@a1xpay.com.' },
]

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(false)
  return (
    <motion.div className={`lp-faq-item${open ? ' lp-faq-open' : ''}`}
      initial={{ opacity: 0, x: 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.07, duration: 0.5 }}>
      <button className="lp-faq-q" onClick={() => setOpen(!open)}>
        <span>{q}</span>
        <motion.span className="lp-faq-icon"
          animate={{ rotate: open ? 180 : 0, backgroundColor: open ? '#1A4DBE' : '#EEF3FF', color: open ? '#fff' : '#1A4DBE' }}
          transition={{ duration: 0.25 }}>
          <ChevronDown size={16} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div className="lp-faq-a-wrap"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}>
            <p className="lp-faq-a">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

const testimonials = [
  { quote: 'A1XPAY built our entire e-commerce payment stack. The architecture is solid and delivery was on time. Zero fluff, just results.', name: 'Aarav Mehta', role: 'Founder, RetailOps', rating: 5 },
  { quote: 'Their API integration team connected all our systems seamlessly. Reliability and uptime have been extraordinary since day one.', name: 'Nisha Verma', role: 'Head of Technology, Finlytics', rating: 5 },
  { quote: 'Best payment infrastructure partner we have worked with. They understand business requirements and deliver enterprise-grade work.', name: 'Karan Singh', role: 'CEO, ShipFast Logistics', rating: 5 },
]

export default function LandingPage() {
  const [hoveredSol, setHoveredSol] = useState(null)

  return (
    <>
      <style>{css}</style>
      <div className="lp-root">
        <Navbar />

        {/* ── HERO ── */}
        <section className="lp-banner">
          {/* Animated orbs */}
          <div className="lp-orb lp-orb-1" />
          <div className="lp-orb lp-orb-2" />
          <div className="lp-orb lp-orb-3" />

          <div className="lp-banner-inner-wrap">
            <div className="lp-container lp-banner-grid">
              <div className="lp-banner-text">
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
                  <motion.span className="lp-hero-badge"
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}>
                    <span className="lp-hero-badge-dot" />
                    Trusted by 200+ Indian Businesses
                  </motion.span>
                  <h1 className="lp-banner-h1">Payment<br />
                    <span className="lp-h1-gradient">Solutions</span>
                  </h1>
                  <motion.p className="lp-banner-p"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    transition={{ delay: 0.35, duration: 0.6 }}>
                    Products and services designed to help you accept payments, build software, and grow your business across India.
                  </motion.p>
                  <motion.div className="lp-banner-ctas"
                    initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.45, duration: 0.5 }}>
                    <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                      <Link to="/register" className="lp-btn-primary">Open Account <ArrowRight size={16} /></Link>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                      <Link to="/contact-us" className="lp-btn-ghost">Get in touch</Link>
                    </motion.div>
                  </motion.div>
                  <motion.div className="lp-trust-pills"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    transition={{ delay: 0.6, duration: 0.5 }}>
                    {['T+1 Settlement', '99.9% Uptime'].map(t => (
                      <span key={t} className="lp-trust-pill"><CheckCircle2 size={13} />{t}</span>
                    ))}
                  </motion.div>
                </motion.div>
              </div>

              <motion.div className="lp-banner-img"
                initial={{ opacity: 0, x: 50, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}>
                <motion.img src={`${B}/banner-bg.png`} alt="A1XPAY platform"
                  onError={e => { e.target.src = `${B}/feature-item-1.png` }}
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }} />
              </motion.div>
            </div>
          </div>

          {/* Partners marquee strip */}
          <div className="lp-marquee-section">
            <p className="lp-marquee-label">Trusted Partners &amp; Integrations</p>
            <div className="lp-marquee-track-wrap">
              <div className="lp-marquee-track">
                {[
                  { name: 'Razorpay', color: '#2D7DD2', icon: '₹' },
                  { name: 'NPCI', color: '#0A4DA1', icon: '🏦' },
                  { name: 'PhonePe', color: '#5F259F', icon: '📱' },
                  { name: 'PayU', color: '#F47B20', icon: '💳' },
                  { name: 'Cashfree', color: '#00A86B', icon: '⚡' },
                  { name: 'Visa', color: '#1A1F71', icon: 'V' },
                  { name: 'Mastercard', color: '#EB001B', icon: 'MC' },
                  { name: 'UPI', color: '#097939', icon: 'UPI' },
                  { name: 'RBI Compliant', color: '#7C3AED', icon: '✓' },
                  { name: 'Razorpay', color: '#2D7DD2', icon: '₹' },
                  { name: 'NPCI', color: '#0A4DA1', icon: '🏦' },
                  { name: 'PhonePe', color: '#5F259F', icon: '📱' },
                  { name: 'PayU', color: '#F47B20', icon: '💳' },
                  { name: 'Cashfree', color: '#00A86B', icon: '⚡' },
                  { name: 'Visa', color: '#1A1F71', icon: 'V' },
                  { name: 'Mastercard', color: '#EB001B', icon: 'MC' },
                  { name: 'UPI', color: '#097939', icon: 'UPI' },
                  { name: 'RBI Compliant', color: '#7C3AED', icon: '✓' },
                ].map((p, i) => (
                  <div key={i} className="lp-marquee-badge">
                    <span className="lp-marquee-badge-icon" style={{ background: p.color + '18', color: p.color }}>{p.icon}</span>
                    <span className="lp-marquee-badge-name" style={{ color: p.color }}>{p.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── STATS ── */}
        <section className="lp-stats-section">
          <div className="lp-container">
            <div className="lp-stats-grid">
              {stats.map((s, i) => (
                <StatCard key={s.label} {...s} />
              ))}
            </div>
          </div>
        </section>

        {/* ── FEATURE 1 – Real-time notifications ── */}
        <section className="lp-section lp-bg-white">
          <div className="lp-container lp-feat-grid">
            <FadeIn x={-40} className="lp-feat-text">
              <motion.div className="lp-head-circle"
                whileInView={{ scale: [0, 1.2, 1] }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}>
                <Bell size={26} color="#fff" />
              </motion.div>
              <span className="lp-sub-title lp-green">Smart Banking</span>
              <h2 className="lp-section-h2">Real time Notifications</h2>
              <p className="lp-para">
                Your customers stay informed in real time with everything happening in their account — payments, transfers, and settlements. Get visibility on your customer flows to anticipate their needs.
              </p>
              <ul className="lp-check-list">
                {['Cards that work all across India.', 'No ATM fees. No minimum balance. No overdrafts.'].map((t, i) => (
                  <motion.li key={t} className="lp-check-item"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.1 }}>
                    <div className="lp-check-dot" /><span>{t}</span>
                  </motion.li>
                ))}
              </ul>
            </FadeIn>
            <FadeIn x={40} delay={0.1} className="lp-feat-img">
              <motion.img src={`${B}/feature-item-1.png`} alt="Notifications"
                whileHover={{ scale: 1.04, rotate: 1 }}
                transition={{ type: 'spring', stiffness: 200 }} />
            </FadeIn>
          </div>
        </section>

        {/* ── FEATURE 2 – Grow ── */}
        <section className="lp-section lp-bg-light">
          <div className="lp-container lp-feat-grid lp-feat-reverse">
            <FadeIn x={-40} delay={0.1} className="lp-feat-img">
              <motion.img src={`${B}/feature-item-2.png`} alt="Grow with A1XPAY"
                whileHover={{ scale: 1.04, rotate: -1 }}
                transition={{ type: 'spring', stiffness: 200 }} />
            </FadeIn>
            <FadeIn x={40} className="lp-feat-text">
              <motion.div className="lp-head-circle"
                whileInView={{ scale: [0, 1.2, 1] }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}>
                <TrendingUp size={26} color="#fff" />
              </motion.div>
              <span className="lp-sub-title lp-green">Business Growth</span>
              <h2 className="lp-section-h2">The Better Way to<br />Accept &amp; Grow</h2>
              <p className="lp-para">
                A1XPAY helps over 200+ businesses achieve their financial goals by helping them accept payments and build growth. Put that business infrastructure to work without putting it at risk.
              </p>
              <ul className="lp-check-list">
                {['Profitable to integrate and easy to manage', 'Highest reliability with 99.9% uptime SLA', 'Open your account from anywhere in India'].map((t, i) => (
                  <motion.li key={t} className="lp-check-item"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.1 }}>
                    <div className="lp-check-dot" /><span>{t}</span>
                  </motion.li>
                ))}
              </ul>
            </FadeIn>
          </div>
        </section>

        {/* ── SOLUTIONS ── */}
        <section className="lp-section lp-bg-white">
          <div className="lp-container">
            <FadeUp>
              <div className="lp-section-head-center">
                <h2 className="lp-section-h2">Solutions for Every Business Need.</h2>
                <p className="lp-para lp-para-center">
                  Power up your business with a full-stack payment and technology platform that fits your needs.
                </p>
              </div>
            </FadeUp>
            <div className="lp-solutions-grid">
              {[
                { icon: <CreditCard size={26} />, title: 'Payment Gateway', desc: 'Accept UPI, cards, wallets, and net banking. Real-time dashboard with T+1 settlements.', cta: 'Get Started', to: '/register', color: '#1A4DBE' },
                { icon: <Link2 size={26} />, title: 'Payment Links', desc: 'Create a payment link in 60 seconds. Share via WhatsApp or embed on your website.', cta: 'Start Now', to: '/payment-link-checkout', color: '#0891B2' },
                { icon: <Code2 size={26} />, title: 'Business API', desc: 'Full REST API with sandbox. Connect your app, ERP, or e-commerce store seamlessly.', cta: 'Explore API', to: '/services/software-development', color: '#7C3AED' },
              ].map((s, i) => (
                <FadeUp key={s.title} delay={i * 0.12}>
                  <TiltCard>
                    <motion.div className="lp-sol-card"
                      onHoverStart={() => setHoveredSol(i)}
                      onHoverEnd={() => setHoveredSol(null)}
                      whileHover={{ y: -6 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}>
                      <motion.div className="lp-sol-icon-wrap"
                        animate={{ backgroundColor: hoveredSol === i ? s.color : '#EEF3FF', color: hoveredSol === i ? '#fff' : s.color }}
                        transition={{ duration: 0.25 }}>
                        {s.icon}
                      </motion.div>
                      <h4 className="lp-sol-title">{s.title}</h4>
                      <p className="lp-sol-desc">{s.desc}</p>
                      <motion.div whileHover={{ x: 4 }}>
                        <Link to={s.to} className="lp-sol-cta">{s.cta} <ArrowRight size={14} /></Link>
                      </motion.div>
                    </motion.div>
                  </TiltCard>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* ── MOBILE / APP ── */}
        <section className="lp-section lp-bg-light">
          <div className="lp-container lp-feat-grid">
            <FadeIn x={-40} className="lp-feat-text">
              <motion.div className="lp-head-circle"
                whileInView={{ scale: [0, 1.2, 1] }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}>
                <Smartphone size={26} color="#fff" />
              </motion.div>
              <span className="lp-sub-title lp-green">Banking at Your Fingertips</span>
              <h2 className="lp-section-h2">Your payment experience anytime, anywhere</h2>
              <p className="lp-para">
                Get your money moving with our simple to use, accessible dashboard and mobile experience. As good as a bank branch within your phone!
              </p>
              <ul className="lp-check-list">
                {['Real-time transaction alerts', 'Instant settlement tracking', 'QR payments & payment links', 'Boost your revenue with smart analytics'].map((t, i) => (
                  <motion.li key={t} className="lp-check-item"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + i * 0.09 }}>
                    <div className="lp-check-dot" /><span>{t}</span>
                  </motion.li>
                ))}
              </ul>
            </FadeIn>
            <FadeIn x={40} delay={0.1} className="lp-feat-img">
              <motion.img src={`${B}/apps.png`} alt="A1XPAY dashboard"
                onError={e => { e.target.src = `${B}/feature-item-1.png` }}
                whileHover={{ scale: 1.04 }}
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }} />
            </FadeIn>
          </div>
        </section>

        {/* ── CTA BANNER ── */}
        <section className="lp-cta-section">
          <div className="lp-cta-glow lp-cta-glow-1" />
          <div className="lp-cta-glow lp-cta-glow-2" />
          <div className="lp-container lp-cta-flex">
            <FadeUp className="lp-cta-text-col">
              <span className="lp-sub-title lp-white">Ready to make the leap?</span>
              <h2 className="lp-cta-h2">Let us help you.</h2>
              <div className="lp-cta-btns">
                <motion.div whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.96 }}>
                  <Link to="/register" className="lp-btn-white">Open Account <ArrowRight size={16} /></Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                  <Link to="/contact-us" className="lp-btn-ghost-w">Get in touch</Link>
                </motion.div>
              </div>
            </FadeUp>
            <motion.div className="lp-cta-illu"
              initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.75, delay: 0.2 }}>
              <img src={`${B}/get-start.png`} alt="Get started illustration" />
            </motion.div>
          </div>
        </section>

        {/* ── PERSONALIZED / DIGITAL SERVICES ── */}
        <section className="lp-section lp-bg-light">
          <div className="lp-container lp-feat-grid lp-feat-reverse">
            <FadeIn x={-40} delay={0.1} className="lp-feat-img">
              <motion.img src={`${B}/personalized.png`} alt="Personalized services"
                onError={e => { e.target.src = `${B}/feature-item-2.png` }}
                whileHover={{ scale: 1.04 }}
                transition={{ type: 'spring', stiffness: 200 }} />
            </FadeIn>
            <FadeIn x={40} className="lp-feat-text">
              <motion.div className="lp-head-circle"
                whileInView={{ scale: [0, 1.2, 1] }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}>
                <Layers size={26} color="#fff" />
              </motion.div>
              <span className="lp-sub-title lp-green">Digital Services</span>
              <h2 className="lp-section-h2">Need a Personalized Solution?</h2>
              <p className="lp-para">
                Get in touch with us, and we will help you create the right technology and marketing solution for your business or personal needs.
              </p>
              <div className="lp-services-grid">
                {[
                  { icon: <Code2 size={18} />, title: 'Software Dev', desc: 'Custom web & mobile apps', to: '/services/software-development' },
                  { icon: <Globe size={18} />, title: 'SEO', desc: 'Rank higher, grow faster', to: '/services/seo' },
                  { icon: <Megaphone size={18} />, title: 'Paid Ads', desc: 'ROAS-focused campaigns', to: '/services/paid-advertisement' },
                  { icon: <Share2 size={18} />, title: 'Social Media', desc: 'Build your brand', to: '/services/social-media' },
                ].map((s, i) => (
                  <motion.div key={s.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    whileHover={{ y: -3, boxShadow: '0 8px 24px rgba(26,77,190,0.15)' }}>
                    <Link to={s.to} className="lp-service-item">
                      <div className="lp-service-icon-wrap">{s.icon}</div>
                      <div>
                        <div className="lp-service-name">{s.title}</div>
                        <div className="lp-service-desc-sm">{s.desc}</div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                <Link to="/contact-us" className="lp-btn-primary">Talk to us <ArrowRight size={16} /></Link>
              </motion.div>
            </FadeIn>
          </div>
        </section>

        {/* ── TESTIMONIALS ── */}
        <section className="lp-section lp-bg-white">
          <div className="lp-container">
            <FadeUp>
              <div className="lp-section-head-center">
                <span className="lp-sub-title lp-green">Testimonials</span>
                <h2 className="lp-section-h2">Don't take our word for it, take theirs</h2>
                <p className="lp-para lp-para-center">
                  Take a look at our past customers' success stories. Our goal is to help you grow.
                </p>
              </div>
            </FadeUp>
            <div className="lp-testi-grid">
              {testimonials.map((t, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 32 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12, duration: 0.6 }}
                  whileHover={{ y: -6, boxShadow: '0 20px 48px rgba(26,77,190,0.14)' }}
                  className="lp-testi-card">
                  <div className="lp-testi-quote">"</div>
                  <div className="lp-testi-stars">
                    {[...Array(t.rating)].map((_, j) => (
                      <motion.span key={j} initial={{ scale: 0 }} whileInView={{ scale: 1 }}
                        viewport={{ once: true }} transition={{ delay: i * 0.12 + j * 0.06 }}>
                        <Star size={14} fill="#F59E0B" color="#F59E0B" />
                      </motion.span>
                    ))}
                  </div>
                  <p className="lp-testi-text">"{t.quote}"</p>
                  <div className="lp-testi-author">
                    <img src={`${B}/testomonial.png`} alt={t.name} className="lp-testi-avatar"
                      onError={e => { e.target.style.background = '#EEF3FF' }} />
                    <div>
                      <div className="lp-testi-name">{t.name}</div>
                      <div className="lp-testi-role">{t.role}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="lp-section lp-bg-light">
          <div className="lp-container">
            <div className="lp-faq-layout">
              <FadeIn x={-40} className="lp-faq-left">
                <span className="lp-sub-title lp-green">If you have question, we have answer</span>
                <h2 className="lp-section-h2">Frequently asked questions</h2>
                <p className="lp-para">
                  Get answers to all questions you have and boost your knowledge so you can save, invest and spend smarter.
                </p>
                <motion.div whileHover={{ x: 4 }}>
                  <Link to="/faq" className="lp-faq-more-link">See all questions here! <ArrowRight size={16} /></Link>
                </motion.div>
              </FadeIn>
              <div className="lp-faq-right">
                {faqs.map((f, i) => <FaqItem key={f.q} q={f.q} a={f.a} index={i} />)}
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;800;900&display=swap');

.lp-root { font-family: 'Jost', sans-serif; background: #fff; overflow-x: hidden; }
.lp-container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }
@media (min-width:1200px) { .lp-container { padding: 0 2.5rem; } }

/* ── Banner ── */
.lp-banner { background: #f5faff; position: relative; overflow: hidden; }
.lp-orb { position: absolute; border-radius: 50%; pointer-events: none; filter: blur(80px); }
.lp-orb-1 { width: 500px; height: 500px; background: rgba(26,77,190,0.09); top: -120px; right: -100px; animation: orbFloat 8s ease-in-out infinite; }
.lp-orb-2 { width: 300px; height: 300px; background: rgba(6,182,212,0.07); bottom: 0; left: -80px; animation: orbFloat 6s ease-in-out infinite reverse; }
.lp-orb-3 { width: 200px; height: 200px; background: rgba(245,158,11,0.06); top: 40%; right: 30%; animation: orbFloat 10s ease-in-out infinite 2s; }
@keyframes orbFloat { 0%,100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-20px) scale(1.05); } }

.lp-banner-inner-wrap { padding: 100px 0 80px; position: relative; z-index: 2; }
.lp-banner-grid { display: grid; grid-template-columns: 1fr; gap: 3rem; align-items: center; }
@media (min-width:1024px) { .lp-banner-grid { grid-template-columns: 6fr 5fr; } }

/* Hero badge */
.lp-hero-badge { display: inline-flex; align-items: center; gap: 0.5rem; background: #fff; border: 1.5px solid #D8DFEE; border-radius: 50px; padding: 0.375rem 1rem; font-size: 0.8125rem; font-weight: 600; color: #1A4DBE; margin-bottom: 1.25rem; box-shadow: 0 2px 12px rgba(26,77,190,0.08); }
.lp-hero-badge-dot { width: 8px; height: 8px; border-radius: 50%; background: #22C55E; flex-shrink: 0; animation: pulse 2s infinite; }
@keyframes pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.4); opacity: 0.6; } }

.lp-sub-title { font-size: 1rem; font-weight: 600; color: #055C2D; margin-bottom: 0.875rem; display: block; }
.lp-green { color: #055C2D; }
.lp-white { color: rgba(255,255,255,0.85); }
.lp-banner-h1 { font-size: clamp(2.5rem, 6vw, 4.5rem); font-weight: 800; color: #0A1023; line-height: 1.08; letter-spacing: -0.04em; margin-bottom: 1.25rem; }
.lp-h1-gradient { background: linear-gradient(135deg, #1A4DBE 0%, #06B6D4 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
.lp-banner-p { font-size: 1.0625rem; color: #2C3655; line-height: 1.7; max-width: 500px; margin-bottom: 2rem; }
.lp-banner-ctas { display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.5rem; }
.lp-trust-pills { display: flex; gap: 0.75rem; flex-wrap: wrap; }
.lp-trust-pill { display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.8125rem; font-weight: 500; color: #2C3655; background: #fff; border: 1px solid #E8EBF4; border-radius: 50px; padding: 0.3rem 0.75rem; }
.lp-trust-pill svg { color: #22C55E; }
.lp-banner-img { display: none; }
@media (min-width:1024px) { .lp-banner-img { display: flex; align-items: center; justify-content: center; } }
.lp-banner-img img { max-width: 100%; }

/* Buttons */
.lp-btn-primary { display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.7rem 1.75rem; font-size: 1rem; font-weight: 600; color: #fff; background: #1A4DBE; border: 1.5px solid #1A4DBE; border-radius: 50px; text-decoration: none; transition: all 0.2s; font-family: 'Jost', sans-serif; white-space: nowrap; cursor: pointer; box-shadow: 0 4px 16px rgba(26,77,190,0.3); }
.lp-btn-primary:hover { background: #1540a8; border-color: #1540a8; box-shadow: 0 8px 24px rgba(26,77,190,0.4); }
.lp-btn-ghost { display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.7rem 1.75rem; font-size: 1rem; font-weight: 600; color: #0A1023; background: transparent; border: 1.5px solid #D0D5E8; border-radius: 50px; text-decoration: none; transition: all 0.2s; font-family: 'Jost', sans-serif; white-space: nowrap; }
.lp-btn-ghost:hover { color: #1A4DBE; border-color: #1A4DBE; background: #EEF3FF; }
.lp-btn-white { display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.7rem 1.75rem; font-size: 1rem; font-weight: 600; color: #1A4DBE; background: #fff; border: 1.5px solid #fff; border-radius: 50px; text-decoration: none; transition: all 0.2s; font-family: 'Jost', sans-serif; white-space: nowrap; box-shadow: 0 4px 16px rgba(0,0,0,0.12); }
.lp-btn-white:hover { background: #f0f4ff; box-shadow: 0 8px 24px rgba(0,0,0,0.16); }
.lp-btn-ghost-w { display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.7rem 1.75rem; font-size: 1rem; font-weight: 600; color: #fff; background: transparent; border: 1.5px solid rgba(255,255,255,0.45); border-radius: 50px; text-decoration: none; transition: all 0.2s; font-family: 'Jost', sans-serif; white-space: nowrap; }
.lp-btn-ghost-w:hover { border-color: #fff; background: rgba(255,255,255,0.1); }

/* Partners Marquee */
.lp-marquee-section { background: #fff; border-top: 1px solid #E8EBF4; border-bottom: 1px solid #E8EBF4; padding: 2.5rem 0; position: relative; overflow: hidden; }
.lp-marquee-section::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 80px; background: linear-gradient(90deg, #fff, transparent); z-index: 2; pointer-events: none; }
.lp-marquee-section::after { content: ''; position: absolute; right: 0; top: 0; bottom: 0; width: 80px; background: linear-gradient(270deg, #fff, transparent); z-index: 2; pointer-events: none; }
.lp-marquee-label { text-align: center; font-size: 0.75rem; font-weight: 700; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.14em; margin-bottom: 1.25rem; }
.lp-marquee-track-wrap { overflow: hidden; }
.lp-marquee-track { display: flex; gap: 1rem; width: max-content; animation: lp-marquee-scroll 28s linear infinite; }
.lp-marquee-track:hover { animation-play-state: paused; }
@keyframes lp-marquee-scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
.lp-marquee-badge { display: inline-flex; align-items: center; gap: 0.6rem; background: #f8faff; border: 1.5px solid #E8EBF4; border-radius: 14px; padding: 0.65rem 1.25rem; transition: background 0.2s, border-color 0.2s, box-shadow 0.2s; cursor: default; white-space: nowrap; }
.lp-marquee-badge:hover { background: #f0f4ff; border-color: #B0BAF1; box-shadow: 0 4px 16px rgba(26,77,190,0.1); }
.lp-marquee-badge-icon { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 8px; font-size: 0.75rem; font-weight: 800; flex-shrink: 0; }
.lp-marquee-badge-name { font-size: 0.875rem; font-weight: 700; color: #2C3655; }

/* ── Stats ── */
.lp-stats-section { background: #fff; padding: 48px 0; border-bottom: 1px solid #F1F3F9; }
.lp-stats-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.25rem; }
@media (min-width:768px) { .lp-stats-grid { grid-template-columns: repeat(4, 1fr); } }
.lp-stat-card { background: #f5faff; border: 1.5px solid #E8EBF4; border-radius: 1rem; padding: 1.5rem 1.25rem; text-align: center; transition: all 0.2s; cursor: default; }
.lp-stat-icon { display: flex; justify-content: center; margin-bottom: 0.75rem; color: #1A4DBE; }
.lp-stat-num { font-size: 2rem; font-weight: 800; color: #0A1023; letter-spacing: -0.04em; line-height: 1; }
.lp-stat-label { font-size: 0.875rem; color: #64748B; font-weight: 500; margin-top: 0.3rem; }

/* Sections */
.lp-section { padding: 90px 0; }
.lp-bg-white { background: #fff; }
.lp-bg-light { background: #f5faff; }

/* Feature grid */
.lp-feat-grid { display: grid; grid-template-columns: 1fr; gap: 3rem; align-items: center; }
@media (min-width:1024px) { .lp-feat-grid { grid-template-columns: 1fr 1fr; gap: 5rem; } }
@media (min-width:1024px) { .lp-feat-reverse .lp-feat-img { order: -1; } }
.lp-feat-img { display: flex; align-items: center; justify-content: center; }
.lp-feat-img img { max-width: 100%; max-height: 420px; object-fit: contain; }
.lp-head-circle { width: 56px; height: 56px; border-radius: 50%; background: linear-gradient(135deg, #1A4DBE 0%, #06B6D4 100%); margin-bottom: 1.5rem; display: flex; align-items: center; justify-content: center; box-shadow: 0 6px 20px rgba(26,77,190,0.3); }
.lp-section-h2 { font-size: clamp(1.75rem, 3.5vw, 2.625rem); font-weight: 700; color: #0A1023; letter-spacing: -0.03em; line-height: 1.2; margin-bottom: 1rem; }
.lp-para { font-size: 1rem; color: #2C3655; line-height: 1.75; margin-bottom: 1.5rem; max-width: 520px; }
.lp-para-center { text-align: center; margin-left: auto; margin-right: auto; max-width: 600px; }
.lp-check-list { list-style: none; padding: 0; margin: 0 0 1.75rem; display: flex; flex-direction: column; gap: 0.75rem; }
.lp-check-item { display: flex; align-items: center; gap: 0.625rem; }
.lp-check-dot { min-width: 22px; min-height: 22px; border-radius: 50%; background: #055C2D; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.lp-check-dot::after { content: ''; width: 8px; height: 5px; border-left: 2px solid #fff; border-bottom: 2px solid #fff; transform: rotate(-45deg); display: block; margin-top: -2px; }
.lp-check-item span { font-size: 1rem; color: #2C3655; font-weight: 500; }
.lp-section-head-center { text-align: center; margin-bottom: 3.5rem; }

/* Solutions */
.lp-solutions-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
@media (min-width:640px) { .lp-solutions-grid { grid-template-columns: repeat(3, 1fr); } }
.lp-sol-card { background: #fff; border-radius: 1.25rem; padding: 2rem 1.5rem; border: 1.5px solid #E8EBF4; display: flex; flex-direction: column; gap: 0.875rem; transition: border-color 0.25s; height: 100%; }
.lp-sol-card:hover { border-color: #B0BAF1; }
.lp-sol-icon-wrap { width: 3.75rem; height: 3.75rem; border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: background-color 0.25s, color 0.25s; flex-shrink: 0; }
.lp-sol-title { font-size: 1.25rem; font-weight: 700; color: #0A1023; }
.lp-sol-desc { font-size: 0.9375rem; color: #2C3655; line-height: 1.65; flex: 1; }
.lp-sol-cta { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.9375rem; font-weight: 600; color: #1A4DBE; text-decoration: none; transition: gap 0.2s; }
.lp-sol-cta:hover { text-decoration: underline; }

/* CTA Section */
.lp-cta-section { background: #1A4DBE; padding: 90px 0; position: relative; overflow: hidden; }
.lp-cta-glow { position: absolute; border-radius: 50%; pointer-events: none; }
.lp-cta-glow-1 { width: 600px; height: 600px; background: rgba(255,255,255,0.06); top: -200px; left: -100px; }
.lp-cta-glow-2 { width: 400px; height: 400px; background: rgba(6,182,212,0.12); bottom: -150px; right: -80px; }
.lp-cta-flex { position: relative; z-index: 2; display: flex; align-items: center; justify-content: space-between; gap: 2rem; flex-wrap: wrap; }
.lp-cta-text-col { flex: 1; min-width: 280px; }
.lp-cta-illu { display: none; }
@media (min-width:900px) { .lp-cta-illu { display: block; width: 220px; flex-shrink: 0; } }
.lp-cta-illu img { max-width: 100%; filter: brightness(1.1) drop-shadow(0 8px 28px rgba(0,0,0,0.25)); }
.lp-cta-h2 { font-size: clamp(2rem, 4vw, 3rem); font-weight: 800; color: #fff; letter-spacing: -0.04em; margin-bottom: 2rem; }
.lp-cta-btns { display: flex; gap: 1rem; flex-wrap: wrap; }
.lp-cta-inner { position: relative; z-index: 2; }

/* Services grid */
.lp-services-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.875rem; margin-bottom: 2rem; }
.lp-service-item { display: flex; align-items: flex-start; gap: 0.75rem; background: #fff; border-radius: 0.875rem; padding: 0.875rem 1rem; box-shadow: 0 2px 12px rgba(47,65,129,0.07); text-decoration: none; transition: box-shadow 0.2s, transform 0.2s; border: 1.5px solid #F1F3F9; }
.lp-service-item:hover { border-color: #B0BAF1; }
.lp-service-icon-wrap { width: 34px; height: 34px; border-radius: 0.45rem; background: #EEF3FF; display: flex; align-items: center; justify-content: center; color: #1A4DBE; flex-shrink: 0; }
.lp-service-name { font-size: 0.9rem; font-weight: 700; color: #0A1023; margin-bottom: 0.2rem; }
.lp-service-desc-sm { font-size: 0.8rem; color: #64748B; line-height: 1.4; }

/* Testimonials */
.lp-testi-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
@media (min-width:768px) { .lp-testi-grid { grid-template-columns: repeat(3, 1fr); } }
.lp-testi-card { background: #fff; border: 1.5px solid #E8EBF4; border-radius: 1.25rem; padding: 1.875rem; box-shadow: 0 4px 16px rgba(47,65,129,0.06); display: flex; flex-direction: column; gap: 0.875rem; transition: border-color 0.2s, box-shadow 0.2s; cursor: default; }
.lp-testi-card:hover { border-color: #B0BAF1; }
.lp-testi-quote { font-size: 3.5rem; font-weight: 900; color: #B0BAF1; line-height: 0.8; font-family: Georgia, serif; }
.lp-testi-stars { display: flex; gap: 0.2rem; }
.lp-testi-text { font-size: 0.9375rem; color: #2C3655; line-height: 1.7; flex: 1; }
.lp-testi-author { display: flex; align-items: center; gap: 0.75rem; margin-top: 0.5rem; }
.lp-testi-avatar { width: 42px; height: 42px; border-radius: 50%; object-fit: cover; background: #EEF3FF; }
.lp-testi-name { font-size: 0.9375rem; font-weight: 700; color: #0A1023; }
.lp-testi-role { font-size: 0.8125rem; color: #64748B; }

/* FAQ */
.lp-faq-layout { display: grid; grid-template-columns: 1fr; gap: 3rem; }
@media (min-width:1024px) { .lp-faq-layout { grid-template-columns: 5fr 7fr; gap: 5rem; align-items: start; } }
.lp-faq-more-link { display: inline-flex; align-items: center; gap: 0.3rem; color: #1A4DBE; font-weight: 600; text-decoration: none; font-size: 1rem; transition: gap 0.2s; }
.lp-faq-more-link:hover { text-decoration: underline; gap: 0.6rem; }
.lp-faq-item { background: #fff; border: 1.5px solid #E8EBF4; border-radius: 0.875rem; margin-bottom: 0.625rem; overflow: hidden; transition: border-color 0.2s, box-shadow 0.2s; }
.lp-faq-open { border-color: #B0BAF1; box-shadow: 0 4px 16px rgba(26,77,190,0.09); }
.lp-faq-q { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 1.125rem 1.25rem; background: none; border: none; cursor: pointer; text-align: left; font-size: 0.9375rem; font-weight: 600; color: #0A1023; font-family: 'Jost', sans-serif; }
.lp-faq-icon { width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.lp-faq-a-wrap { overflow: hidden; }
.lp-faq-a { padding: 0 1.25rem 1.125rem; font-size: 0.9rem; color: #2C3655; line-height: 1.7; margin: 0; font-family: 'Jost', sans-serif; }
`
